import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  Refresh as RefreshIcon,
  ReportProblem as ReportProblemIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon
} from '@mui/icons-material';
import { resourceService } from '../services/resourceService';
import { FileViewer, getFileType, type FileType } from './FileViewer';
import type { Resource } from '../types';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from './ToastNotification';
import { logger } from '../utils/logger';


interface ResourceViewerProps {
  resource: Resource;
  open: boolean;
  onClose: () => void;
  onDownload?: (resource: Resource) => void;
  // When used in public share page, provide token and set mode to 'public'
  mode?: 'auth' | 'public';
  publicToken?: string | null;
}

export const ResourceViewer: React.FC<ResourceViewerProps> = ({
  resource,
  open,
  onClose,
  onDownload,
  mode = 'auth',
  publicToken = null
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<FileType>('unsupported');
  const [scale, setScale] = useState(1.0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageInput, setPageInput] = useState('');
  const viewerRef = useRef<HTMLDivElement>(null);
  const { snackbar, showSuccess, hideSnackbar } = useSnackbar();


  useEffect(() => {
    if (open && resource) {
      loadResource();
    }
  }, [open, resource]);

  const loadResource = async () => {
    if (!resource) return;

    setLoading(true);
    setError(null);

    try {
      // Determine file type using helper function
      // Use resource.fileType if fileName doesn't have extension
      const fileNameForType = resource.fileName || '';
      const resourceFileType = resource.fileType || '';
      
      // Use fileType field if fileName doesn't have extension
      let detectedFileType = getFileType(fileNameForType);
      if (detectedFileType === 'unsupported' && resourceFileType) {
        // Try using resource.fileType (e.g., ".docx")
        detectedFileType = getFileType(`file${resourceFileType}`);
      }
      
      setFileType(detectedFileType);
      
      const fileTypeLower = resource.fileType.toLowerCase();
      const isVideo = ['.mp4', '.avi', '.mov', '.wmv', '.webm'].includes(fileTypeLower);
      const isAudio = ['.mp3', '.wav', '.flac', '.aac', '.ogg'].includes(fileTypeLower);
      
      // For audio and video, use OS native viewer (keep existing behavior)
      if (isAudio || isVideo) {
        // Increment view count
        await resourceService.viewResource(resource.id);
        
        // Get file URL and open in new window/tab for OS to handle
        const blob = await resourceService.getResourceFile(resource.id);
        const url = URL.createObjectURL(blob);
        
        // Open in new window - OS will handle with native viewer
        const newWindow = window.open(url, '_blank');
        if (newWindow) {
          // Clean up after window closes (approximate)
          setTimeout(() => {
            URL.revokeObjectURL(url);
          }, 1000);
        } else {
          // If popup blocked, fall back to download
          const a = document.createElement('a');
          a.href = url;
          a.download = resource.fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
        
        // Close the dialog since we opened in new window
        handleClose();
        return;
      }
      
      // For PDF, DOCX, XLSX, Office, Image - use FileViewer component
      if (detectedFileType === 'pdf' || detectedFileType === 'docx' || detectedFileType === 'xlsx' || detectedFileType === 'office' || detectedFileType === 'image') {
        // Two modes:
        // - auth: in-app viewing uses authenticated endpoints
        // - public: share page viewing uses public endpoints WITH token
        if (mode === 'public') {
          if (!publicToken) {
            setError('Link này yêu cầu token để truy cập. Vui lòng sử dụng link được tạo từ nút "Copy".');
            setLoading(false);
            return;
          }

          // Best-effort: increment view count via public endpoint (tokenized)
          try {
            await resourceService.viewResourcePublic(resource.id, publicToken);
          } catch (_) {}

          // For public viewing, always use public file endpoint with token (no auth headers)
          const baseUrl = window.location.origin;
          const publicApiUrl = `${baseUrl}/api/resources/public/${resource.id}/file?token=${encodeURIComponent(publicToken)}`;

          if (detectedFileType === 'office') {
            setError('Loại file Office này không được hỗ trợ xem trực tiếp. Vui lòng tải về để xem.');
            setLoading(false);
            return;
          }

          setFileUrl(publicApiUrl);
          if (detectedFileType === 'pdf') setCurrentPage(0);
          setLoading(false);
          return;
        }

        // auth mode: increment view count and fetch file via authenticated endpoint
        await resourceService.viewResource(resource.id);
        const blob = await resourceService.getResourceFile(resource.id);
        const objectUrl = URL.createObjectURL(blob);
        
        if (detectedFileType === 'pdf') {
          // PDF: render from object URL
          setFileUrl(objectUrl);
          setCurrentPage(0);
        } else if (detectedFileType === 'docx') {
          // DOCX: mammoth.js can fetch object URL
          setFileUrl(objectUrl);
        } else if (detectedFileType === 'xlsx') {
          // XLSX: xlsx lib can fetch object URL
          setFileUrl(objectUrl);
        } else if (detectedFileType === 'office') {
          // Office Online Viewer requires a publicly accessible URL (no auth headers).
          // For security, we do NOT expose authenticated resources to third-party viewers.
          // => Require download to view.
          URL.revokeObjectURL(objectUrl);
          setError('Loại file Office này không được hỗ trợ xem trực tiếp. Vui lòng tải về để xem.');
        } else if (detectedFileType === 'image') {
          // Image: render from object URL
          setFileUrl(objectUrl);
        }
        
        setLoading(false);
      } else {
        // For non-viewable files, don't increment view count - only increment when user downloads
        setError('Loại file này không được hỗ trợ xem trực tiếp. Vui lòng tải về để xem.');
        setLoading(false);
      }
    } catch (err) {
      const errorMessage = err instanceof Error && err.message === 'FILE_NOT_FOUND'
        ? 'FILE_NOT_FOUND'
        : err instanceof Error ? err.message : 'Không thể tải tài nguyên';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (onDownload) {
      onDownload(resource);
    } else {
      // Default download behavior
      try {
        const blob = await resourceService.downloadResource(resource.id);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = resource.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        logger.error('Error downloading resource:', err);
        setError('Failed to download resource');
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (numPages && currentPage < numPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleGoToPage = () => {
    const pageNum = parseInt(pageInput);
    if (!isNaN(pageNum) && numPages && pageNum >= 1 && pageNum <= numPages) {
      setCurrentPage(pageNum - 1);
      setPageInput('');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    try {
      // Generate share token first
      const tokenData = await resourceService.getShareToken(resource.id);
      const token = tokenData.token;
      const shareableLink = `${window.location.origin}/public/resource/${resource.id}?token=${encodeURIComponent(token)}`;
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: resource.title,
            text: `Xem tài liệu: ${resource.title}`,
            url: shareableLink
          });
        } catch (err) {
          // User cancelled or error
        }
      } else {
        // Fallback: copy to clipboard with fallback method
        // Try modern Clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
          try {
            await navigator.clipboard.writeText(shareableLink);
            showSuccess('Đã sao chép liên kết vào clipboard');
            return;
          } catch (clipboardError) {
            logger.warn('Clipboard API failed, trying fallback:', clipboardError);
            // Fall through to fallback method
          }
        }
        
        // Fallback method for environments without Clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = shareableLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            showSuccess('Đã sao chép liên kết vào clipboard');
          } else {
            throw new Error('execCommand failed');
          }
        } catch (fallbackError) {
          logger.error('Fallback copy method failed:', fallbackError);
          // Last resort: show the link in a prompt
          window.prompt('Không thể tự động sao chép. Vui lòng sao chép link sau:', shareableLink);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (err) {
      logger.error('Error sharing resource:', err);
      showError('Không thể tạo link chia sẻ');
    }
  };

  const handleRefresh = async () => {
    // Clear current file URL and reload
    if (fileUrl) {
      URL.revokeObjectURL(fileUrl);
      setFileUrl(null);
    }
    await loadResource();
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleClose = () => {
    // Clean up blob URL (only if it's a blob URL)
    if (fileUrl && fileUrl.startsWith('blob:')) {
      URL.revokeObjectURL(fileUrl);
    }
    setFileUrl(null);
    setScale(1.0);
    setIsFullscreen(false);
    setError(null);
    setCurrentPage(0);
    setNumPages(null);
    onClose();
  };

  const handleReportError = async () => {
    if (!resource) return;
    
    try {
      setReporting(true);
      await resourceService.reportFileError(resource.id, 'File không tồn tại trên server');
      setReportSent(true);
      showSuccess('Đã gửi báo cáo thành công. Cảm ơn bạn đã phản hồi!');
    } catch (err) {
      // Silently fail - don't show error to user
    } finally {
      setReporting(false);
    }
  };

  const fileTypeLower = resource.fileType.toLowerCase();
  const isVideo = ['.mp4', '.avi', '.mov', '.wmv', '.webm'].includes(fileTypeLower);
  const isAudio = ['.mp3', '.wav', '.flac', '.aac', '.ogg'].includes(fileTypeLower);
  const isViewable = fileType === 'pdf' || fileType === 'docx' || fileType === 'xlsx' || fileType === 'office' || fileType === 'image' || isVideo || isAudio;

  // Auto fullscreen for PDF, DOCX, XLSX and Office files
  useEffect(() => {
    if (open && (fileType === 'pdf' || fileType === 'docx' || fileType === 'xlsx' || fileType === 'office')) {
      setIsFullscreen(true);
    } else if (!open) {
      setIsFullscreen(false);
    }
  }, [open, fileType]);

  // Keyboard shortcuts for PDF
  useEffect(() => {
    if (open && fileType === 'pdf') {
      const handleKeyDown = (e: KeyboardEvent) => {
        // Arrow keys for navigation
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          handlePreviousPage();
        }
        else if (e.key === 'ArrowRight') {
          e.preventDefault();
          handleNextPage();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, fileType, currentPage, numPages]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={(fileType === 'pdf' || fileType === 'docx' || fileType === 'xlsx' || fileType === 'office' || fileType === 'image' || isFullscreen) ? false : 'lg'}
      fullWidth={!(fileType === 'pdf' || fileType === 'docx' || fileType === 'xlsx' || fileType === 'office' || fileType === 'image')}
      fullScreen={(fileType === 'pdf' || fileType === 'docx' || fileType === 'xlsx' || fileType === 'office' || fileType === 'image' || isFullscreen)}
      PaperProps={(fileType === 'pdf' || fileType === 'docx' || fileType === 'xlsx' || fileType === 'office' || fileType === 'image') ? {
        sx: {
          width: '100vw',
          height: '100vh',
          maxWidth: '100vw',
          maxHeight: '100vh',
          m: 0,
          borderRadius: 0
        }
      } : {
        sx: { height: isFullscreen ? '100vh' : '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {resource.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(fileType === 'pdf' || fileType === 'docx' || fileType === 'xlsx' || fileType === 'office') && (
            <>
              <Tooltip title="Làm mới">
                <IconButton onClick={handleRefresh} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title="Toàn màn hình">
            <IconButton onClick={toggleFullscreen} size="small">
              <FullscreenIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Tải về">
            <IconButton onClick={handleDownload} size="small">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Đóng">
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ 
        p: 0, 
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: '400px',
        backgroundColor: '#ff00ff', // Magenta background to see DialogContent
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              minHeight: 400
            }}
          >
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Đang tải tài nguyên...
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            {error === 'FILE_NOT_FOUND' ? (
              <>
                <Alert 
                  severity="warning" 
                  icon={<ReportProblemIcon />}
                  sx={{ mb: 2 }}
                  action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={handleRefresh}
                        disabled={loading}
                      >
                        Thử lại
                      </Button>
                      <Button 
                        color="inherit" 
                        size="small" 
                        startIcon={<ReportProblemIcon />}
                        onClick={handleReportError}
                        disabled={reporting || reportSent}
                      >
                        {reportSent ? 'Đã gửi' : 'Gửi báo cáo'}
                      </Button>
                    </Box>
                  }
                >
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    Không tìm thấy file
                  </Typography>
                  <Typography variant="body2">
                    File này có thể đã bị xóa hoặc không tồn tại trên server. Vui lòng liên hệ quản trị viên nếu bạn cần hỗ trợ.
                  </Typography>
                </Alert>
              </>
            ) : (
              <Alert severity="error" action={
                <Button color="inherit" size="small" onClick={handleRefresh}>
                  Thử lại
                </Button>
              }>
                {error}
              </Alert>
            )}
          </Box>
        )}

        {/* Use FileViewer component for PDF, DOCX, XLSX, Office, and Image */}
        {!loading && !error && (fileType === 'pdf' || fileType === 'docx' || fileType === 'xlsx' || fileType === 'office' || fileType === 'image') && fileUrl && (
          <Box
            ref={viewerRef}
            sx={{
              width: '100%',
              height: '100%',
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* FileViewer component - handles PDF, Office, Image rendering */}
            <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
              <FileViewer
                fileUrl={fileUrl}
                fileName={resource.fileName}
                fileType={fileType}
                loading={false}
                error={null}
                onPageChange={handlePageChange}
                onNumPagesChange={setNumPages}
                currentPage={currentPage}
                numPages={numPages}
                showNavigation={fileType === 'pdf'} // Only show navigation for PDF
                fullHeight={true}
              />
            </Box>

            {/* Custom Toolbar for PDF - with additional features (print, share, go to page) */}
            {fileType === 'pdf' && numPages && (
              <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                zIndex: 10,
                gap: 2
              }}>
                {/* Navigation buttons */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Tooltip title="Trang trước">
                    <IconButton 
                      onClick={handlePreviousPage} 
                      disabled={currentPage === 0}
                      sx={{ color: 'white' }}
                      size="small"
                    >
                      <NavigateBeforeIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Trang sau">
                    <IconButton 
                      onClick={handleNextPage} 
                      disabled={numPages ? currentPage >= numPages - 1 : true}
                      sx={{ color: 'white' }}
                      size="small"
                    >
                      <NavigateNextIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Page info and input */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'white', minWidth: '80px', textAlign: 'center' }}>
                    {numPages ? `${currentPage + 1} / ${numPages}` : '...'}
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Trang"
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleGoToPage();
                      }
                    }}
                    sx={{
                      width: '60px',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                      },
                      '& input': {
                        padding: '8px',
                        textAlign: 'center'
                      }
                    }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleGoToPage}
                    sx={{ minWidth: 'auto', px: 1.5 }}
                  >
                    Đi đến
                  </Button>
                </Box>

                {/* Control buttons */}
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Tooltip title="In">
                    <IconButton onClick={handlePrint} sx={{ color: 'white' }} size="small">
                      <PrintIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Chia sẻ">
                    <IconButton onClick={handleShare} sx={{ color: 'white' }} size="small">
                      <ShareIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Toàn màn hình">
                    <IconButton onClick={toggleFullscreen} sx={{ color: 'white' }} size="small">
                      <FullscreenIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {!loading && !error && fileType !== 'pdf' && fileType !== 'docx' && fileType !== 'xlsx' && fileType !== 'office' && fileType !== 'image' && !isViewable && (
          <Box
            ref={viewerRef}
            sx={{
              width: '100%',
              height: '100%',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: 2
            }}
          >
          </Box>
        )}

        {!loading && !error && !isViewable && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              minHeight: 400,
              p: 4
            }}
          >
            <Typography variant="h6" gutterBottom>
              Loại File Không Được Hỗ Trợ Xem
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Loại file này ({resource.fileType}) không thể xem trực tiếp. Vui lòng tải về để xem.
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Tải File
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Đóng
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Tải về
        </Button>
      </DialogActions>

      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Dialog>
  );
};

export default ResourceViewer;
